/**
 * Utilitaires d'accessibilité pour l'amélioration de la page d'accueil
 * Implémente les exigences: 1.1, 1.2, 3.1, 4.2
 * 
 * Fonctionnalités:
 * - Navigation clavier complète
 * - Annonces ARIA pour les changements d'état
 * - Support des lecteurs d'écran
 * - Gestion du contraste et redimensionnement
 */

/**
 * Interface pour les annonces ARIA
 */
export interface AriaAnnouncement {
  message: string;
  priority: 'polite' | 'assertive';
  delay?: number;
}

/**
 * Interface pour les options de navigation clavier
 */
export interface KeyboardNavigationOptions {
  trapFocus?: boolean;
  skipLinks?: boolean;
  arrowNavigation?: boolean;
  escapeHandler?: () => void;
}

/**
 * Service d'annonces ARIA pour les lecteurs d'écran
 */
export class AriaAnnouncementService {
  private static instance: AriaAnnouncementService;
  private liveRegions: Map<string, HTMLElement> = new Map();

  private constructor() {
    this.createLiveRegions();
  }

  static getInstance(): AriaAnnouncementService {
    if (!AriaAnnouncementService.instance) {
      AriaAnnouncementService.instance = new AriaAnnouncementService();
    }
    return AriaAnnouncementService.instance;
  }

  /**
   * Crée les régions live pour les annonces ARIA
   */
  private createLiveRegions(): void {
    // Région pour les annonces polies
    const politeRegion = document.createElement('div');
    politeRegion.setAttribute('aria-live', 'polite');
    politeRegion.setAttribute('aria-atomic', 'true');
    politeRegion.className = 'sr-only';
    politeRegion.id = 'aria-live-polite';
    document.body.appendChild(politeRegion);
    this.liveRegions.set('polite', politeRegion);

    // Région pour les annonces assertives
    const assertiveRegion = document.createElement('div');
    assertiveRegion.setAttribute('aria-live', 'assertive');
    assertiveRegion.setAttribute('aria-atomic', 'true');
    assertiveRegion.className = 'sr-only';
    assertiveRegion.id = 'aria-live-assertive';
    document.body.appendChild(assertiveRegion);
    this.liveRegions.set('assertive', assertiveRegion);
  }

  /**
   * Annonce un message aux lecteurs d'écran
   */
  announce(announcement: AriaAnnouncement): void {
    const region = this.liveRegions.get(announcement.priority);
    if (!region) return;

    const delay = announcement.delay || 100;

    // Vider la région d'abord pour forcer l'annonce
    region.textContent = '';

    setTimeout(() => {
      region.textContent = announcement.message;
      
      // Vider après 5 secondes pour éviter l'accumulation
      setTimeout(() => {
        if (region.textContent === announcement.message) {
          region.textContent = '';
        }
      }, 5000);
    }, delay);
  }

  /**
   * Annonce les résultats de recherche
   */
  announceSearchResults(count: number, hasFilters: boolean = false): void {
    let message: string;
    
    if (count === 0) {
      message = hasFilters ? 
        'Aucun résultat ne correspond aux filtres appliqués' :
        'Aucun résultat trouvé pour cette recherche';
    } else if (count === 1) {
      message = hasFilters ?
        '1 résultat correspond aux filtres appliqués' :
        '1 résultat trouvé';
    } else {
      message = hasFilters ?
        `${count} résultats correspondent aux filtres appliqués` :
        `${count} résultats trouvés`;
    }

    this.announce({
      message,
      priority: 'polite',
      delay: 500
    });
  }

  /**
   * Annonce les changements de page
   */
  announcePageChange(currentPage: number, totalPages: number): void {
    this.announce({
      message: `Page ${currentPage} sur ${totalPages}`,
      priority: 'polite'
    });
  }

  /**
   * Annonce les erreurs
   */
  announceError(errorMessage: string): void {
    this.announce({
      message: `Erreur: ${errorMessage}`,
      priority: 'assertive'
    });
  }

  /**
   * Annonce les changements de filtres
   */
  announceFilterChange(filterType: string, value: string, isActive: boolean): void {
    const action = isActive ? 'appliqué' : 'supprimé';
    this.announce({
      message: `Filtre ${filterType} ${value} ${action}`,
      priority: 'polite'
    });
  }

  /**
   * Annonce les suggestions d'autocomplétion
   */
  announceSuggestions(count: number): void {
    if (count === 0) {
      this.announce({
        message: 'Aucune suggestion disponible',
        priority: 'polite'
      });
    } else {
      this.announce({
        message: `${count} suggestion${count > 1 ? 's' : ''} disponible${count > 1 ? 's' : ''}`,
        priority: 'polite'
      });
    }
  }
}

/**
 * Gestionnaire de navigation clavier
 */
export class KeyboardNavigationManager {
  private focusableElements: HTMLElement[] = [];
  private currentFocusIndex = -1;
  private container: HTMLElement;
  private options: KeyboardNavigationOptions;
  private originalFocus: HTMLElement | null = null;

  constructor(container: HTMLElement, options: KeyboardNavigationOptions = {}) {
    this.container = container;
    this.options = {
      trapFocus: false,
      skipLinks: true,
      arrowNavigation: false,
      ...options
    };

    this.updateFocusableElements();
    this.bindEvents();
  }

  /**
   * Met à jour la liste des éléments focusables
   */
  private updateFocusableElements(): void {
    const focusableSelectors = [
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'a[href]',
      '[tabindex]:not([tabindex="-1"])',
      '[role="button"]:not([disabled])',
      '[role="link"]',
      '[role="menuitem"]',
      '[role="option"]'
    ].join(', ');

    this.focusableElements = Array.from(
      this.container.querySelectorAll(focusableSelectors)
    ) as HTMLElement[];

    // Filtrer les éléments cachés
    this.focusableElements = this.focusableElements.filter(el => {
      return this.isElementVisible(el) && !this.isElementDisabled(el);
    });
  }

  /**
   * Vérifie si un élément est visible
   */
  private isElementVisible(element: HTMLElement): boolean {
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && 
           style.visibility !== 'hidden' && 
           style.opacity !== '0' &&
           element.offsetParent !== null;
  }

  /**
   * Vérifie si un élément est désactivé
   */
  private isElementDisabled(element: HTMLElement): boolean {
    return element.hasAttribute('disabled') || 
           element.getAttribute('aria-disabled') === 'true';
  }

  /**
   * Lie les événements de navigation clavier
   */
  private bindEvents(): void {
    this.container.addEventListener('keydown', this.handleKeyDown.bind(this));
    
    if (this.options.trapFocus) {
      this.container.addEventListener('focusin', this.handleFocusIn.bind(this));
    }
  }

  /**
   * Gère les événements de touche
   */
  private handleKeyDown(event: KeyboardEvent): void {
    const { key, target } = event;

    switch (key) {
      case 'Tab':
        if (this.options.trapFocus) {
          this.handleTabNavigation(event);
        }
        break;

      case 'ArrowDown':
      case 'ArrowUp':
        if (this.options.arrowNavigation) {
          event.preventDefault();
          this.handleArrowNavigation(key === 'ArrowDown' ? 1 : -1);
        }
        break;

      case 'Home':
        if (this.options.arrowNavigation) {
          event.preventDefault();
          this.focusFirst();
        }
        break;

      case 'End':
        if (this.options.arrowNavigation) {
          event.preventDefault();
          this.focusLast();
        }
        break;

      case 'Escape':
        if (this.options.escapeHandler) {
          event.preventDefault();
          this.options.escapeHandler();
        }
        break;

      case 'Enter':
      case ' ':
        // Gérer l'activation des éléments avec rôle button
        if (target instanceof HTMLElement && target.getAttribute('role') === 'button') {
          event.preventDefault();
          target.click();
        }
        break;
    }
  }

  /**
   * Gère la navigation par tabulation avec piège de focus
   */
  private handleTabNavigation(event: KeyboardEvent): void {
    if (this.focusableElements.length === 0) return;

    const firstElement = this.focusableElements[0];
    const lastElement = this.focusableElements[this.focusableElements.length - 1];

    if (event.shiftKey) {
      // Shift + Tab - navigation arrière
      if (document.activeElement === firstElement) {
        event.preventDefault();
        lastElement?.focus();
      }
    } else {
      // Tab - navigation avant
      if (document.activeElement === lastElement) {
        event.preventDefault();
        firstElement?.focus();
      }
    }
  }

  /**
   * Gère la navigation par flèches
   */
  private handleArrowNavigation(direction: number): void {
    this.updateFocusableElements();
    
    if (this.focusableElements.length === 0) return;

    const currentElement = document.activeElement as HTMLElement;
    this.currentFocusIndex = this.focusableElements.indexOf(currentElement);

    let newIndex = this.currentFocusIndex + direction;

    // Boucler si nécessaire
    if (newIndex >= this.focusableElements.length) {
      newIndex = 0;
    } else if (newIndex < 0) {
      newIndex = this.focusableElements.length - 1;
    }

    this.focusableElements[newIndex]?.focus();
    this.currentFocusIndex = newIndex;
  }

  /**
   * Gère l'événement focusin pour le piège de focus
   */
  private handleFocusIn(event: FocusEvent): void {
    const target = event.target as HTMLElement;
    
    if (!this.container.contains(target)) {
      // Le focus est sorti du conteneur, le ramener
      event.preventDefault();
      this.focusFirst();
    }
  }

  /**
   * Focus sur le premier élément
   */
  focusFirst(): void {
    this.updateFocusableElements();
    if (this.focusableElements.length > 0) {
      this.focusableElements[0]?.focus();
      this.currentFocusIndex = 0;
    }
  }

  /**
   * Focus sur le dernier élément
   */
  focusLast(): void {
    this.updateFocusableElements();
    if (this.focusableElements.length > 0) {
      const lastIndex = this.focusableElements.length - 1;
      this.focusableElements[lastIndex]?.focus();
      this.currentFocusIndex = lastIndex;
    }
  }

  /**
   * Active le piège de focus
   */
  trapFocus(): void {
    this.originalFocus = document.activeElement as HTMLElement;
    this.options.trapFocus = true;
    this.focusFirst();
  }

  /**
   * Désactive le piège de focus
   */
  releaseFocus(): void {
    this.options.trapFocus = false;
    if (this.originalFocus) {
      this.originalFocus.focus();
      this.originalFocus = null;
    }
  }

  /**
   * Met à jour les options
   */
  updateOptions(newOptions: Partial<KeyboardNavigationOptions>): void {
    this.options = { ...this.options, ...newOptions };
  }

  /**
   * Nettoie les événements
   */
  destroy(): void {
    this.container.removeEventListener('keydown', this.handleKeyDown.bind(this));
    this.container.removeEventListener('focusin', this.handleFocusIn.bind(this));
  }
}

/**
 * Utilitaires d'accessibilité généraux
 */
export class AccessibilityUtils {
  /**
   * Ajoute des attributs ARIA appropriés à un élément
   */
  static setAriaAttributes(element: HTMLElement, attributes: Record<string, string>): void {
    Object.entries(attributes).forEach(([key, value]) => {
      const ariaKey = key.startsWith('aria-') ? key : `aria-${key}`;
      element.setAttribute(ariaKey, value);
    });
  }

  /**
   * Crée un ID unique pour les associations ARIA
   */
  static generateId(prefix: string = 'aria'): string {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Associe un label à un contrôle
   */
  static associateLabel(control: HTMLElement, label: HTMLElement): void {
    const labelId = label.id || this.generateId('label');
    label.id = labelId;
    control.setAttribute('aria-labelledby', labelId);
  }

  /**
   * Associe une description à un contrôle
   */
  static associateDescription(control: HTMLElement, description: HTMLElement): void {
    const descId = description.id || this.generateId('desc');
    description.id = descId;
    
    const existingDescribedBy = control.getAttribute('aria-describedby');
    const newDescribedBy = existingDescribedBy ? 
      `${existingDescribedBy} ${descId}` : descId;
    
    control.setAttribute('aria-describedby', newDescribedBy);
  }

  /**
   * Vérifie si les préférences de mouvement réduit sont activées
   */
  static prefersReducedMotion(): boolean {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Vérifie si le mode contraste élevé est activé
   */
  static prefersHighContrast(): boolean {
    return window.matchMedia('(prefers-contrast: high)').matches;
  }

  /**
   * Applique les préférences d'accessibilité
   */
  static applyAccessibilityPreferences(): void {
    const body = document.body;

    // Mouvement réduit
    if (this.prefersReducedMotion()) {
      body.classList.add('reduce-motion');
    }

    // Contraste élevé
    if (this.prefersHighContrast()) {
      body.classList.add('high-contrast');
    }

    // Écouter les changements de préférences
    window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', (e) => {
      body.classList.toggle('reduce-motion', e.matches);
    });

    window.matchMedia('(prefers-contrast: high)').addEventListener('change', (e) => {
      body.classList.toggle('high-contrast', e.matches);
    });
  }

  /**
   * Améliore l'accessibilité d'un formulaire
   */
  static enhanceFormAccessibility(form: HTMLFormElement): void {
    const inputs = form.querySelectorAll('input, select, textarea');
    
    inputs.forEach(input => {
      const inputElement = input as HTMLInputElement;
      
      // Associer les labels
      const label = form.querySelector(`label[for="${inputElement.id}"]`) as HTMLLabelElement;
      if (label && !inputElement.getAttribute('aria-labelledby')) {
        this.associateLabel(inputElement, label);
      }

      // Associer les messages d'erreur
      const errorElement = form.querySelector(`[data-error-for="${inputElement.id}"]`) as HTMLElement;
      if (errorElement) {
        this.associateDescription(inputElement, errorElement);
      }

      // Associer l'aide
      const helpElement = form.querySelector(`[data-help-for="${inputElement.id}"]`) as HTMLElement;
      if (helpElement) {
        this.associateDescription(inputElement, helpElement);
      }

      // Ajouter les attributs requis
      if (inputElement.hasAttribute('required')) {
        inputElement.setAttribute('aria-required', 'true');
      }
    });
  }



  /**
   * Améliore l'accessibilité des boutons
   */
  static enhanceButtonAccessibility(button: HTMLElement): void {
    // S'assurer que le bouton a un rôle approprié
    if (!button.getAttribute('role') && button.tagName !== 'BUTTON') {
      button.setAttribute('role', 'button');
    }

    // S'assurer que le bouton est focusable
    if (!button.hasAttribute('tabindex') && button.tagName !== 'BUTTON') {
      button.setAttribute('tabindex', '0');
    }

    // Ajouter la gestion du clavier pour les éléments avec rôle button
    if (button.getAttribute('role') === 'button' && button.tagName !== 'BUTTON') {
      button.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          button.click();
        }
      });
    }
  }
}

/**
 * Instance globale du service d'annonces ARIA
 */
export const ariaAnnouncer = AriaAnnouncementService.getInstance();